import { useState, useEffect, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, TruckIcon } from '@heroicons/react/24/outline';
import { getShipmentsForCustomer } from '@/services/firebase';
import { useAuth } from '@/hooks/useAuth';
import { Shipment } from '@/types';
import LoadingSpinner from '@/components/LoadingSpinner';

/** Firestore may hand back a Timestamp, a Date, or a string. */
function formatDate(value: unknown): string {
  if (!value) return '—';
  const raw =
    typeof value === 'object' && value !== null && 'toDate' in value
      ? (value as { toDate: () => Date }).toDate()
      : new Date(value as string | number | Date);
  return isNaN(raw.getTime()) ? '—' : raw.toLocaleDateString();
}

/** Older shipments predate the destination field, so don't print ", undefined". */
function routeLabel(shipment: Shipment): string {
  const from = [shipment.origin?.city, shipment.origin?.country].filter(Boolean).join(', ');
  const to = [shipment.destination?.city, shipment.destination?.country].filter(Boolean).join(', ');
  if (from && to) return `${from} → ${to}`;
  if (to) return `To ${to}`;
  if (from) return `From ${from}`;
  return 'Destination not set';
}

const STATUS_STYLES: Record<string, string> = {
  delivered: 'bg-green-100 text-green-800',
  in_transit: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
  pending: 'bg-yellow-100 text-yellow-800',
};

export default function CustomerShipments() {
  const { user } = useAuth();
  const [shipments, setShipments] = useState<Shipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Query comes from the topbar search as well as this page's own box.
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get('q') || '';

  const setQuery = (value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set('q', value);
    else next.delete('q');
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (!user) return;

    const fetchShipments = async () => {
      try {
        setLoading(true);
        const customerShipments = await getShipmentsForCustomer(user.id);
        setShipments(customerShipments);
        setError(null);
      } catch (err) {
        console.error("Error fetching customer shipments:", err);
        setError('Failed to load shipments. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchShipments();
  }, [user]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return shipments;
    return shipments.filter((s) => {
      const haystack = [
        s.trackingNumber,
        s.status,
        s.destination?.city,
        s.destination?.street,
        ...(s.items?.map((i) => i.description) || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [shipments, query]);

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="text-center text-red-500">{error}</div>;
  }

  return (
    <div className="container mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">My Shipments</h1>
        <Link
          to="/portal/shipments/new"
          className="btn-primary inline-flex items-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Shipment
        </Link>
      </div>

      {shipments.length > 0 && (
        <div className="relative mb-6">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by tracking number, item, or destination…"
            aria-label="Search your shipments"
            className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-4 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
          />
        </div>
      )}

      {shipments.length > 0 && visible.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow-md">
          <MagnifyingGlassIcon className="mx-auto h-10 w-10 text-gray-300" />
          <p className="mt-3 text-gray-600">
            No shipments match &ldquo;{query}&rdquo;.
          </p>
          <button
            onClick={() => setQuery('')}
            className="mt-3 text-sm font-medium text-blue-600 hover:underline"
          >
            Clear search
          </button>
        </div>
      ) : shipments.length === 0 ? (
        <div className="rounded-lg bg-white py-12 text-center shadow-md">
          <TruckIcon className="mx-auto h-12 w-12 text-gray-300" />
          <h3 className="mt-3 text-base font-medium text-gray-900">No shipments yet</h3>
          <p className="mt-1 mb-5 text-gray-500">
            Create your first shipment and we&apos;ll take it from there.
          </p>
          <Link
            to="/portal/shipments/new"
            className="btn-primary inline-flex items-center"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Your First Shipment
          </Link>
        </div>
      ) : (
        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <ul className="divide-y divide-gray-200">
            {visible.map((shipment) => (
              <li key={shipment.id}>
                <Link to={`/portal/shipments/${shipment.id}`} className="block p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start gap-4">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-blue-600">
                        Tracking: #{shipment.trackingNumber}
                      </p>
                      <p className="text-lg font-medium text-gray-900">
                        {routeLabel(shipment)}
                      </p>
                      {shipment.items && shipment.items.length > 0 && (
                        <p className="mt-0.5 truncate text-sm text-gray-500">
                          {shipment.items.map((i) => i.description).filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0 text-right">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize ${
                          STATUS_STYLES[shipment.status] || 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {String(shipment.status || '').replace('_', ' ')}
                      </span>
                      <p className="mt-2 text-sm text-gray-500">{formatDate(shipment.createdAt)}</p>
                    </div>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
} 